import { X } from 'lucide-react'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type MutableRefObject,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'

import { HeroSunDiscGrain } from '@/components/hero-sun-disc'
import { Button } from '@/components/ui/button'
import { usePrefersReducedMotion } from '@/hooks/use-prefers-reduced-motion'
import {
  RISE_TIMINGS,
  sunriseOverlayStyles,
  useSunriseOverlayTimeline,
  type SunriseOverlayPhase,
  type SunriseOverlayTimings,
} from '@/hooks/use-sunrise-overlay-timeline'
import { bigSunFillStyles, type BigSunBounds } from '@/lib/big-sun-frame'
import { flushPendingPageHashScroll } from '@/lib/scroll-to-page-hash'
import { cn } from '@/lib/utils'

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'

function useFocusTrap(
  containerRef: React.RefObject<HTMLElement | null>,
  active: boolean,
  /** Extra root(s) included in the tab cycle (e.g. site header when chrome is external). */
  extraRootSelector?: string,
) {
  useEffect(() => {
    if (!active) return

    const container = containerRef.current
    if (!container) return

    const collectFocusable = () => {
      const roots: HTMLElement[] = []
      if (extraRootSelector) {
        const extra = document.querySelector<HTMLElement>(extraRootSelector)
        if (extra) roots.push(extra)
      }
      roots.push(container)
      return roots.flatMap((root) => [
        ...root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ])
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return

      const focusable = collectFocusable()
      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (event.shiftKey) {
        if (document.activeElement === first) {
          event.preventDefault()
          last?.focus()
        }
        return
      }

      if (document.activeElement === last) {
        event.preventDefault()
        first?.focus()
      }
    }

    // Document listener so Tab from external header chrome is trapped too.
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [active, containerRef, extraRootSelector])
}

function readOverlayBounds(): BigSunBounds {
  return {
    sectionW: window.innerWidth,
    sectionH: window.innerHeight,
    vw: window.innerWidth,
  }
}

export type SunriseOverlayRenderApi = {
  screenFilled: boolean
  phase: SunriseOverlayPhase
  animateBorders: boolean
  handleClose: () => void
}

type SunriseOverlayProps = {
  id?: string
  onClosed: () => void
  labelledBy: string
  closeAriaLabel: string
  /** CSS selector within the panel to focus once the screen is filled. */
  autoFocusSelector?: string
  /** Open-animation timings. Defaults to the cinematic Get in Touch rise. */
  timings?: SunriseOverlayTimings
  /**
   * When true, omit the overlay’s own top chrome (logo / actions / close).
   * Use when a sticky site header above the overlay owns those controls.
   */
  hideHeader?: boolean
  /** Imperative close — e.g. site header menu button while `hideHeader` is set. */
  closeRef?: MutableRefObject<(() => void) | null>
  /**
   * Skip `createPortal` so the overlay shares the site header’s stacking context
   * (needed when the page root uses `isolate` and the nav must sit above the menu).
   */
  disablePortal?: boolean
  /** Content rendered at the top-left of the overlay (e.g. logo mark). */
  headerLeading?: ReactNode | ((api: SunriseOverlayRenderApi) => ReactNode)
  /** Extra controls rendered to the left of the close button. */
  headerActions?: ReactNode | ((api: SunriseOverlayRenderApi) => ReactNode)
  /** Width constraint for the header bar when `headerLeading` is set. */
  headerBarClassName?: string
  panelClassName?: string
  className?: string
  children: (api: SunriseOverlayRenderApi) => ReactNode
}

export function SunriseOverlay({
  id,
  onClosed,
  labelledBy,
  closeAriaLabel,
  autoFocusSelector,
  timings = RISE_TIMINGS,
  hideHeader = false,
  closeRef,
  disablePortal = false,
  headerLeading,
  headerActions,
  headerBarClassName,
  panelClassName,
  className,
  children,
}: SunriseOverlayProps) {
  const panelRef = useRef<HTMLElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const [bounds, setBounds] = useState<BigSunBounds>(readOverlayBounds)
  /** When site header owns chrome, pin panel content to the bar’s bottom edge. */
  const [headerOffsetPx, setHeaderOffsetPx] = useState(0)
  const prefersReducedMotion = usePrefersReducedMotion()

  const { progress, exitOpacity, phase, playClose } = useSunriseOverlayTimeline({
    active: true,
    timings,
    onCloseComplete: onClosed,
  })

  const styles = sunriseOverlayStyles(progress, bounds, timings)
  const interactive = styles.screenFilled && phase !== 'closing'
  const fillStyles = bigSunFillStyles(styles.sunFrame.fillPhaseT)
  const animateBorders = !prefersReducedMotion

  useLayoutEffect(() => {
    const updateBounds = () => {
      setBounds(readOverlayBounds())
    }

    updateBounds()
    window.addEventListener('resize', updateBounds)
    return () => window.removeEventListener('resize', updateBounds)
  }, [])

  useLayoutEffect(() => {
    if (!hideHeader) {
      setHeaderOffsetPx(0)
      return
    }

    const bar = document.querySelector<HTMLElement>('[data-site-header-bar]')
    if (!bar) return

    const updateOffset = () => {
      setHeaderOffsetPx(Math.ceil(bar.getBoundingClientRect().bottom))
    }

    updateOffset()
    const observer = new ResizeObserver(updateOffset)
    observer.observe(bar)
    // Parent padding transitions move the bar without resizing it.
    const chrome = bar.parentElement
    chrome?.addEventListener('transitionend', updateOffset)
    window.addEventListener('resize', updateOffset)

    let frames = 0
    let rafId = 0
    const tick = () => {
      updateOffset()
      frames += 1
      // Cover the header padding transition (~300ms) after open.
      if (frames < 24) rafId = window.requestAnimationFrame(tick)
    }
    rafId = window.requestAnimationFrame(tick)

    return () => {
      observer.disconnect()
      chrome?.removeEventListener('transitionend', updateOffset)
      window.removeEventListener('resize', updateOffset)
      window.cancelAnimationFrame(rafId)
    }
  }, [hideHeader])

  useFocusTrap(
    panelRef,
    interactive,
    hideHeader ? '[data-site-header-bar]' : undefined,
  )

  useLayoutEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
      // Hash jumps while the lock was on often apply only after unlock, and
      // may never emit `scroll` — re-assert a queued target, then nudge chrome.
      flushPendingPageHashScroll()
      window.dispatchEvent(new Event('scroll'))
    }
  }, [])

  const handleClose = useCallback(() => {
    if (phase === 'closing' || phase === 'idle') return
    playClose()
  }, [phase, playClose])

  useEffect(() => {
    if (!closeRef) return
    closeRef.current = handleClose
    return () => {
      closeRef.current = null
    }
  }, [closeRef, handleClose])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        handleClose()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleClose])

  useEffect(() => {
    if (!interactive) return

    if (autoFocusSelector) {
      const target = panelRef.current?.querySelector<HTMLElement>(autoFocusSelector)
      target?.focus()
      return
    }

    closeButtonRef.current?.focus()
  }, [autoFocusSelector, interactive])

  // The sun box stays at its largest size and the frame is drawn with
  // clip-path. Growing width/height instead moved the box every frame,
  // which browsers count as layout shift (CLS) when /contact deep-links
  // straight into this animation with no user input.
  const { width: sunW, height: sunH, borderRadius: sunR } = styles.sunFrame
  const sunBoxW = Math.max(bounds.sectionW, bounds.sectionH * 2)
  const sunBoxH = bounds.sectionH
  const sunInsetX = Math.max(0, (sunBoxW - sunW) / 2)
  const sunInsetTop = Math.max(0, sunBoxH - sunH)
  const sunStyle: CSSProperties = {
    width: sunBoxW,
    height: sunBoxH,
    clipPath: `inset(${sunInsetTop}px ${sunInsetX}px 0 ${sunInsetX}px round ${sunR}px ${sunR}px 0 0)`,
  }

  const overlay = (
    <div
      id={id}
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
      className={cn(
        'sunrise-overlay get-in-touch-overlay fixed inset-0 z-[100] overflow-hidden',
        className,
      )}
      data-phase={phase}
      data-screen-filled={styles.screenFilled ? '' : undefined}
      style={{ opacity: exitOpacity }}
    >
      <div
        aria-hidden
        className="big-sun pointer-events-none absolute bottom-0 left-1/2 z-0 -translate-x-1/2 overflow-hidden"
        style={sunStyle}
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundColor: fillStyles.backgroundColor,
            boxShadow: fillStyles.boxShadow,
          }}
        >
          <HeroSunDiscGrain />
        </div>
      </div>

      <section
        ref={panelRef}
        className={cn(
          'relative z-10 flex h-[100svh] w-full flex-col overflow-y-auto px-6 text-paper',
          // Site header owns top chrome — top inset comes from measured bar height.
          hideHeader ? 'pb-16' : 'py-16',
        )}
        style={hideHeader ? { paddingTop: headerOffsetPx } : undefined}
        aria-labelledby={labelledBy}
      >
        {hideHeader ? null : headerLeading ? (
          <div
            className={cn(
              'absolute top-4 right-6 left-6 z-20',
              !styles.screenFilled && 'pointer-events-none opacity-0',
            )}
          >
            <div
              className={cn(
                'mx-auto flex w-full items-center justify-between gap-3',
                headerBarClassName,
              )}
            >
              {typeof headerLeading === 'function'
                ? headerLeading({
                    screenFilled: styles.screenFilled,
                    phase,
                    animateBorders,
                    handleClose,
                  })
                : headerLeading}
              <div className="flex items-center gap-2">
                {typeof headerActions === 'function'
                  ? headerActions({
                      screenFilled: styles.screenFilled,
                      phase,
                      animateBorders,
                      handleClose,
                    })
                  : headerActions}
                <Button
                  ref={closeButtonRef}
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={closeAriaLabel}
                  onClick={handleClose}
                  className="size-10 rounded-xl border border-white/20 bg-white/10 text-paper backdrop-blur-sm hover:bg-white/20 hover:text-white"
                >
                  <X className="size-5" aria-hidden />
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div
            className={cn(
              'absolute top-4 right-4 z-20 flex items-center gap-2',
              !styles.screenFilled && 'pointer-events-none opacity-0',
            )}
          >
            {typeof headerActions === 'function'
              ? headerActions({
                  screenFilled: styles.screenFilled,
                  phase,
                  animateBorders,
                  handleClose,
                })
              : headerActions}
            <Button
              ref={closeButtonRef}
              type="button"
              variant="ghost"
              size="icon"
              aria-label={closeAriaLabel}
              onClick={handleClose}
              className="size-10 rounded-xl border border-white/20 bg-white/10 text-paper backdrop-blur-sm hover:bg-white/20 hover:text-white"
            >
              <X className="size-5" aria-hidden />
            </Button>
          </div>
        )}

        <div
          className={cn(
            'sunrise-overlay__panel get-in-touch-overlay__panel relative z-10 mx-auto w-full',
            panelClassName,
          )}
          style={
            {
              '--git-content-opacity': styles.contentOpacity,
              '--git-content-translate-y': `${styles.contentTranslateY}px`,
              '--git-content-blur': `${styles.contentBlur}px`,
            } as CSSProperties
          }
        >
          {children({
            screenFilled: styles.screenFilled,
            phase,
            animateBorders,
            handleClose,
          })}
        </div>
      </section>
    </div>
  )

  if (disablePortal) {
    return overlay
  }

  return createPortal(overlay, document.body)
}
